import { animate, style, transition, trigger } from '@angular/animations';
import { Component, computed, input, OnDestroy, OnInit, output, signal } from '@angular/core';
import { MODAL_ANIMATION } from '@features/styles/animations';
import { Wk8PaperPieceComponent } from '@features/wk8/core/wk8-paper-piece/wk8-paper-piece.component';
import { ILeesVariant } from '@features/wk8/mechanics/wk8-lees/variants/lees-variant.interface';
import { waitForMillis } from '@shared/delay.utils';
import { calcArrayDiff } from '@shared/misc.utils';
import { WordToKlankConverter } from '@shared/woordkr8/WordToKlankConverter';

@Component({
  selector: 'wk8-lees',
  imports: [Wk8PaperPieceComponent],
  templateUrl: './wk8-lees.component.html',
  styleUrl: './wk8-lees.component.css',
  animations: [
    MODAL_ANIMATION(false),
    trigger('klankChange', [
      transition(':enter', [style({ transform: 'scale(0)' }), animate('0.25s ease-out', style({ transform: 'scale(1)' }))]),
      transition(':leave', [style({ transform: 'scale(1)' }), animate('0.25s ease-out', style({ transform: 'scale(0)' }))]),
      transition('* => *', [
        style({ transform: 'translateY(-40px)', opacity: 0 }),
        animate('0.5s ease-out', style({ transform: 'translateY(0)', opacity: 1 })),
      ]),
    ]),
  ],
})
export class Wk8LeesComponent implements OnInit, OnDestroy {
  variant = input.required<ILeesVariant>();
  finished = output();

  protected index = signal(-100);
  protected timePerKlank = signal<number>(500);
  protected parts = signal<{ klank: string; id: string; highlight: boolean }[]>([]);
  protected mode = signal<'ZOEM' | 'HAK'>('ZOEM');
  protected isCondensed = signal(false);
  protected isMetronomeVisible = signal(false);
  protected fontSize = computed(() =>
    Math.min(
      5,
      5 /
        ((5 +
          this.parts()
            .map((p) => p.klank)
            .join('   ').length) /
          25),
    ),
  );

  async ngOnInit(): Promise<void> {
    await this.variant().init(this);

    this.variant().start();
  }

  async ngOnDestroy(): Promise<void> {
    await this.variant().stop();
  }

  public async readPass(msPerKlank: number) {
    this.timePerKlank.set(msPerKlank);
    this.index.set(this.isMetronomeVisible() ? -2 : 0);

    while (true) {
      if (this.index() >= this.parts().length) {
        break;
      }

      await waitForMillis(this.timePerKlank());

      this.index.set(this.index() + 1);
    }
  }

  public async unhighlight() {
    this.index.set(-100);
    await waitForMillis(this.timePerKlank());
  }

  public async condense(condense: boolean) {
    this.isCondensed.set(condense);
    await waitForMillis(250); // wait for transition
  }

  public async showMetronome(show: boolean) {
    this.isMetronomeVisible.set(show);
    await waitForMillis(250); // wait for transition
  }

  public async clear() {
    await Promise.all([
      this.unhighlight(), //
      this.setWord(''),
      this.showMetronome(false),
    ]);
  }

  public async setWord(newWord: string | string[], swap: boolean = true) {
    const oldWord = this.parts().map((p) => p.klank);
    if (oldWord.length === 0 || !swap) {
      return await this.setWordInstantly(newWord);
    }

    if (newWord.length === 0) {
      await this.setWordInstantly([]);
    }

    const diff = calcArrayDiff(oldWord, typeof newWord === 'string' ? WordToKlankConverter.breakupWord(newWord) : newWord);
    for (let i = 0; i < diff.length; i++) {
      const d = diff[i];

      if (d.type === 'replace') {
        this.parts.set(this.parts().map((p, idx) => (idx === d.index ? { klank: d.newValue, id: p.id, highlight: p.highlight } : p)));
      }
      if (d.type === 'insert') {
        this.parts.set([
          ...this.parts().slice(0, d.index),
          { klank: d.value, id: `${Math.random()}`, highlight: false },
          ...this.parts().slice(d.index),
        ]);
        for (let j = i + 1; j < diff.length; j++) {
          diff[j].index++;
        }
      }
      if (d.type === 'remove') {
        this.parts.set([...this.parts().slice(0, d.index), ...this.parts().slice(d.index + 1)]);
        for (let j = i + 1; j < diff.length; j++) {
          diff[j].index--;
        }
      }
    }

    await waitForMillis(500); // wait for transition
  }

  private async setWordInstantly(part: string | string[]) {
    this.parts.set(
      typeof part === 'string'
        ? [{ klank: part, id: `${Math.random()}`, highlight: false }]
        : part.map((klank) => ({ klank, id: `${Math.random()}`, highlight: false })),
    );
    await waitForMillis(250); // wait for transition
  }

  public finish() {
    this.finished.emit();
  }

  protected hasHighlight(index: number) {
    switch (this.mode()) {
      case 'HAK':
        return index === this.index();
      case 'ZOEM':
        return index <= this.index();
      default:
        throw new Error(`Unknown mode: ${this.mode()}`);
    }
  }
}
